import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Alert, Button, Container, FormControl, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from 'dayjs';

import { AppConfig } from "../../../config/appConfig";
import { useUserContext } from "../../../contexts/userContext";
import { ErrorProvider } from "../../../classes/errorProvider";

import { ILoad_DeletedLight } from "../../../types/deleted";

import { DeletedService } from "../../../services/deletedService"

import DeletedLoadTable from "../../tables/deleted/deletedLoadTable";
import { Icons } from "../../icons";
import Navigation from "../../header";
import PermissionGate from "../../permissionGate";
import Spinner from "../../spinner";
import WorkingModal, { IWorkingModalProps } from "../../workingModal";

import "../../../styles/deleted/deletedLoadList.scss";

const DeletedLoadList = () => {
	const navigate = useNavigate();
	const userContext = useUserContext();
	const [gridView, setGridView] = useState<ILoad_DeletedLight[]>();
	const [isRestoringLoad, setIsRestoringLoad] = useState(false);	

	//Date range
	const [startDate, setStartDate] = useState<Date | undefined>(undefined);
	const [endDate, setEndDate] = useState<Date | undefined>(undefined);

	//Filtering
	const [customerOptions, setCustomerOptions] = useState<string[]>([]);
	const [vendorOptions, setVendorOptions] = useState<string[]>([]);
	const [statusOptions, setStatusOptions] = useState<string[]>([]);

	const [trailerNumberFilter, setTrailerNumberFilter] = useState("");
	const [bolFilter, setBolFilter] = useState("");
	const [customerFilter, setCustomerFilter] = useState("All");
	const [vendorFilter, setVendorFilter] = useState("All");
	const [statusFilter, setStatusFilter] = useState("All");

	const [isLoadingLoads, setIsLoadingLoads] = useState(false);
	const [successMessage, setSuccessMessage] = useState<string | React.ReactNode | undefined>();
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [restoringErrorMessage, setRestoringErrorMessage] = useState<string | undefined>();
	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);

	useEffect(() => {
		let end = new Date();
		setEndDate(end);

		let start = new Date(end);
  		start.setDate(end.getDate() - 90);
		setStartDate(start);
	}, [])

	useEffect(() => {
		loadLoadData();
	}, [startDate])

	const loadLoadData = async () => {
		if (startDate == undefined) return;
		setIsLoadingLoads(true);
		setErrorMessage(undefined);

		let customers: number[] = [];
		userContext.tilsAccess.map((item) => {
			customers.push(item.customerId);
		});

		DeletedService.getDeletedLoads(startDate, customers)
			.then((response) => {
				console.log('DeletedService.getDeletedLoads: SUCCESS', response);

				let customers: string[] = [];
				let vendors: string[] = [];
				let statuses: string[] = [];

				let loads = DeletedService.mapLoadLightList(response.data);

				loads.map((item) => {
					if (item.customerName && !customers.includes(item.customerName)) {
						customers.push(item.customerName);
					}

					if (item.vendorName && !vendors.includes(item.vendorName)) {
						vendors.push(item.vendorName);
					}

					if (item.status && !statuses.includes(item.status)) {
						statuses.push(item.status);
					}
				});

				setGridView(loads);
				setCustomerOptions(customers.sort());
				setVendorOptions(vendors.sort());
				setStatusOptions(statuses.sort());
				
				//For TILS users
				if (statuses.includes("Loading") && userContext.permissions.canManageShipments == false) {
					setStatusFilter("Loading");
				}

				//For HQ users
				if (statuses.includes("Shipped") && userContext.permissions.canManageShipments == true) {
					setStatusFilter("Shipped");
				}
	
			})
			.catch((error) => {
				console.log('DeletedService.getDeletedLoads: ERROR', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
				else {
					ErrorProvider.getError(error);
				}
			})
			.finally(() => {
				setIsLoadingLoads(false);
			});
	}

	const handleStartDateChange = (value: Dayjs | null) => {
		if (value) {
			setStartDate(value.toDate());
		}
	}

	const handleEndDateChange = (value: Dayjs | null) => {
		if (value) {
			setEndDate(value.toDate());
		}
	}

	const handleRestoreLoadRequest = (loadId: number, bol?: string) => {
		setRestoringErrorMessage(undefined);
		setIsRestoringLoad(true);
		setWorkingModalParams({ params: { content: `Restoring load${bol ? ` "${bol}"` : ""}...` } });

		DeletedService.softRestoreDeletedLoad(loadId)
			.then((response) => {
				console.log('DeletedService.softRestoreDeletedLoad: SUCCESS', response);
				setGridView(prev => prev.filter(record => record.id !== loadId));
				setSuccessMessage(<>{`Load ${bol ? `"${bol}"` : loadId} has been successfully restored.`} <NavLink className="open-restored" to={"/tils/" + loadId}><Icons.ViewItem fill="#1e4620" height="14px"></Icons.ViewItem><span>View</span></NavLink></>);
			})
			.catch((error) => {				
				console.log('DeletedService.softRestoreDeletedLoad: ERROR', error);
				setRestoringErrorMessage(ErrorProvider.getError(error))
			})
			.finally(() => {
				setIsRestoringLoad(false);
				setWorkingModalParams(undefined);
			});
	}

	return (
		<div className="hwi-deleted-load-list">
			<Navigation></Navigation>
			<Container maxWidth="lg" className="route-container">
				<PermissionGate permission={userContext.permissions?.canManageLoads}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<section className="hwi-entity-search-bar">
							<div className="search-header">
								<div className="title">Search Deleted Loads</div>
								<div className="controls">
									<div className="date-field">
										<DatePicker className="start-date-field" label="Created on or after" value={dayjs(startDate)} onChange={(value) => handleStartDateChange(value)} />
									</div>
									<div className="button">
										<Button variant="outlined" fullWidth={true} onClick={loadLoadData} >Search</Button>
									</div>
								</div>
							</div>
							<section className="search-options">
								<FormControl size="small">
									<TextField className="bol-field" label="BOL #" name="bol" value={bolFilter} size="small" onChange={(event) => { setBolFilter(event.target.value) }}></TextField>
								</FormControl>
								<FormControl size="small">
									{/* <InputLabel>Trailer Number</InputLabel> */}
									<TextField className="trailer-field" label="Trailer #" name="trailer" value={trailerNumberFilter} size="small" onChange={(event) => { setTrailerNumberFilter(event.target.value) }}></TextField>
								</FormControl>
								<FormControl size="small">
									<InputLabel>Customer</InputLabel>
									<Select
										value={customerFilter}
										defaultValue="All"
										label="Customer"
										onChange={(event: SelectChangeEvent) => { setCustomerFilter(event.target.value) }}
									>
										<MenuItem value="All" key="-1">All</MenuItem>
										{customerOptions.map((item, index) => (
											<MenuItem value={item} key={index}>{item}</MenuItem>
										))}
									</Select>
								</FormControl>
								<FormControl size="small">
									<InputLabel>Vendor</InputLabel>
									<Select
										value={vendorFilter}
										label="Vendor"
										onChange={(event: SelectChangeEvent) => { setVendorFilter(event.target.value) }}
									>
										<MenuItem value="All" key="-1">All</MenuItem>
										{vendorOptions.map((item, index) => (
											<MenuItem value={item} key={index}>{item}</MenuItem>
										))}
									</Select>
								</FormControl>
								
								<FormControl size="small">
									<InputLabel>Status</InputLabel>
									<Select
										value={statusFilter}
										label="Status"
										onChange={(event: SelectChangeEvent) => { setStatusFilter(event.target.value) }}
									>
										<MenuItem value="All" key="-1">All</MenuItem>
										{statusOptions.map((item, index) => (
											<MenuItem value={item} key={index}>{item}</MenuItem>
										))}
									</Select>
								</FormControl>
							</section>
						</section>
						<section className="results">
							<Spinner dependency={isLoadingLoads} text="Retrieving Loads...">
								{successMessage &&
									<Alert className="alert-success"
										severity="success"
										action={<IconButton onClick={() => { setSuccessMessage(undefined); }} size='small'>X</IconButton>}>
										{successMessage}
									</Alert>
								}
								{errorMessage &&
									<Alert className="alert-error"
										severity="error">
										{errorMessage}
									</Alert>
								}
								{restoringErrorMessage &&
									<Alert className="alert-error"
										severity="error">
										{restoringErrorMessage}
									</Alert>
								}
								{!errorMessage &&
									<DeletedLoadTable 
										data={gridView} 
										customer={customerFilter} 
										vendor={vendorFilter} 
										trailer={trailerNumberFilter} 
										hwiNumber={bolFilter} 
										status={statusFilter}
										onRestoreLoadRequest={handleRestoreLoadRequest}
										></DeletedLoadTable>
								}								
							</Spinner>
						</section>
						{/* <Json data={userContext}></Json> */}
					</LocalizationProvider>
				</PermissionGate>
				<WorkingModal params={workingModalParams?.params}></WorkingModal>
			</Container>
		</div>
	);
};

export default DeletedLoadList;